import { type FC, useCallback, type ChangeEvent } from 'react';
import { FormattedMessage, FormattedNumber, defineMessages, useIntl } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import TranslationToggle, { type TranslationState } from 'dibs-elements/exports/TranslationToggle';
import { StarRating } from 'dibs-elements/exports/StarRating';
import { SellerReviewsSummary } from 'dibs-seller-reviews-modal';
import dibsCss from 'dibs-css';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { TabList } from 'dibs-elements/exports/TabList';
import { trackEvent } from 'dibs-tracking';
import serverVars from 'server-vars';

import { ORDER_BY_NEWEST, ORDER_BY_HIGHEST, ORDER_BY_LOWEST, type OrderBy } from '../../constants';

import { type SellerReviewsModalHeader_viewer$key } from './__generated__/SellerReviewsModalHeader_viewer.graphql';
import { type SellerReviewsModalHeader_reviews$key } from './__generated__/SellerReviewsModalHeader_reviews.graphql';

import styles from './SellerReviewsModalHeader.scss';

export const REVIEWS_TAB = 'REVIEWS_TAB';
export const PHOTOS_TAB = 'PHOTOS_TAB';

const trackingLabelMap: Record<string, string> = {
    [ORDER_BY_NEWEST]: 'featured',
    [ORDER_BY_HIGHEST]: 'high to low',
    [ORDER_BY_LOWEST]: 'low to high',
};

const orderOptionLabels = defineMessages({
    [ORDER_BY_NEWEST]: {
        id: 'SellerReviewsModalHeader.option.label.mostRecent',
        defaultMessage: 'Most Recent',
    },
    [ORDER_BY_HIGHEST]: {
        id: 'SellerReviewsModalHeader.option.label.highToLow',
        defaultMessage: 'High to Low',
    },
    [ORDER_BY_LOWEST]: {
        id: 'SellerReviewsModalHeader.option.label.lowToHigh',
        defaultMessage: 'Low to High',
    },
});

const viewerFragment = graphql`
    fragment SellerReviewsModalHeader_viewer on Viewer {
        feedbackFormPhotoUpload: featureFlag(feature: "feedbackFormPhotoUpload")
        #Variables must match SellerReviewsPhotosCarousel_orderFeedbackMultiple connection variables
        reviewsWithPhotos: orderFeedbackMultiple(
            sellerId: $sellerId
            excludeInAppealProcess: true
            orderByNewest: true
            excludePrivateItemReviews: true
            hasVisibleFeedbackImages: true
            first: 0
            page: 0
        ) {
            totalResults
        }
    }
`;

const reviewsFragment = graphql`
    fragment SellerReviewsModalHeader_reviews on OrderFeedbackMultipleQueryConnection {
        totalResults
    }
`;

type Props = {
    viewer: SellerReviewsModalHeader_viewer$key | null | undefined;
    reviews: SellerReviewsModalHeader_reviews$key | null | undefined;
    activeTab: string;
    translationState: TranslationState;
    avgOverallRating?: number | null;
    onTranslateChange: (arg: TranslationState) => void;
    onSortOrderChange: (arg: OrderBy) => void;
    isCompact?: boolean;
    isMobile?: boolean;
    noTabs?: boolean;
};

export const SellerReviewsModalHeader: FC<Props> = ({
    translationState,
    avgOverallRating,
    onTranslateChange,
    onSortOrderChange,
    activeTab,
    isCompact,
    isMobile,
    noTabs,
    ...props
}) => {
    const intl = useIntl();
    const viewer = useFragment(viewerFragment, props.viewer);
    const reviews = useFragment(reviewsFragment, props.reviews);

    const feedbackFormPhotoUpload = viewer?.feedbackFormPhotoUpload;
    const totalReviews = reviews?.totalResults;
    const reviewsWithPhotosCount = viewer?.reviewsWithPhotos?.totalResults;
    const showTotalReviews =
        (noTabs && typeof totalReviews === 'number') ||
        (!noTabs && !feedbackFormPhotoUpload && typeof totalReviews === 'number');

    const shouldShowTabs = !!feedbackFormPhotoUpload && typeof totalReviews === 'number' && !noTabs;

    const onChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>): void => {
            const value = e.target.value;

            onSortOrderChange(value as OrderBy);
            trackEvent({
                category: 'seller reviews',
                action: 'seller reviews sort selected',
                label: trackingLabelMap[value],
            });
        },
        [onSortOrderChange]
    );

    const onTabActive = useCallback((value: string): void => {
        if (value === PHOTOS_TAB && serverVars.get('settings.pageType') === 'pdp') {
            trackEvent({
                category: 'seller reviews',
                action: 'seller reviews photos tab clicked',
                label: 'pdp',
            });
        }
    }, []);

    const onTranslate = (): void => {
        if (translationState === 'SHOW_ORIGINAL') {
            onTranslateChange('SHOW_TRANSLATION');
        } else {
            onTranslateChange('SHOW_ORIGINAL');
        }
    };

    return (
        <HeadingLevel>
            {Heading => (
                <>
                    <div
                        className={classnames(styles.headerWrapper, {
                            [styles.compact]: isCompact,
                            [styles.mobile]: isMobile,
                            [styles.noTabs]: noTabs,
                        })}
                    >
                        <div
                            className={classnames(styles.headerDetails, {
                                [styles.compact]: isCompact,
                            })}
                        >
                            {!noTabs && (
                                <Heading
                                    className={classnames(styles.header, {
                                        [styles.compact]: isCompact,
                                    })}
                                >
                                    <FormattedMessage
                                        id="SellerReviewsModalHeader.title"
                                        defaultMessage="Seller Reviews"
                                    />
                                </Heading>
                            )}
                            {(noTabs || !isCompact) && (
                                <div
                                    className={styles.subtitle}
                                    data-tn="seller-review-modal-subtitle"
                                >
                                    {!!avgOverallRating && (
                                        <>
                                            {noTabs ? (
                                                <SellerReviewsSummary
                                                    rating={avgOverallRating || 0}
                                                    totalReviews={
                                                        showTotalReviews ? totalReviews : null
                                                    }
                                                    variant="inline"
                                                    className={classnames(
                                                        styles.ratingText,
                                                        dibsCss.mrXsmall
                                                    )}
                                                    ratingTextClassName={
                                                        dibsCss.sassyFontBodyLargeHeavy
                                                    }
                                                    showTotalReviews={showTotalReviews}
                                                />
                                            ) : (
                                                <>
                                                    <StarRating
                                                        rating={avgOverallRating || 0}
                                                        size="x-small"
                                                        inline
                                                    />
                                                    <span className={styles.ratingText}>
                                                        <FormattedNumber
                                                            style="decimal"
                                                            value={avgOverallRating || 0}
                                                            minimumFractionDigits={1}
                                                        />
                                                    </span>
                                                    {showTotalReviews && (
                                                        <span className={styles.totalReviews}>
                                                            <FormattedMessage
                                                                id="SellerReviewsModalHeader.totalReviews"
                                                                defaultMessage={`{totalReviews, number} {totalReviews, plural,one {Review} other {Reviews}}`}
                                                                values={{ totalReviews }}
                                                            />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        {activeTab === REVIEWS_TAB && (
                            <>
                                <div
                                    className={classnames(styles.selectWrapper, {
                                        [styles.compact]: isCompact,
                                        [styles.mobile]: isMobile,
                                        [styles.reviewVariant]: noTabs,
                                    })}
                                >
                                    <BasicSelect
                                        size="medium"
                                        dataTn="sort-order"
                                        name="basic-select"
                                        onChange={onChange}
                                        options={[
                                            {
                                                value: ORDER_BY_NEWEST,
                                                label: intl.formatMessage(
                                                    orderOptionLabels[ORDER_BY_NEWEST]
                                                ),
                                            },
                                            {
                                                value: ORDER_BY_HIGHEST,
                                                label: intl.formatMessage(
                                                    orderOptionLabels[ORDER_BY_HIGHEST]
                                                ),
                                            },
                                            {
                                                value: ORDER_BY_LOWEST,
                                                label: intl.formatMessage(
                                                    orderOptionLabels[ORDER_BY_LOWEST]
                                                ),
                                            },
                                        ]}
                                    />
                                </div>
                                {!noTabs && (
                                    <TranslationToggle
                                        className={styles.translationToggle}
                                        currentState={translationState}
                                        onClick={onTranslate}
                                    />
                                )}
                            </>
                        )}

                        {shouldShowTabs && (
                            <div
                                className={classnames(styles.tabsWrapper, {
                                    [styles.compact]: isCompact,
                                })}
                            >
                                <TabList
                                    underline={false}
                                    activeFont="smallBodyHeavy"
                                    inactiveFont="smallBody"
                                    onTabActivate={onTabActive}
                                >
                                    {[
                                        REVIEWS_TAB,
                                        <FormattedMessage
                                            key={REVIEWS_TAB}
                                            id="SellerReviewsModalTabs.reviewTab"
                                            defaultMessage="{totalReviews, select, 0 {No Reviews} 1 {{totalReviews} Review} other {{totalReviews} Reviews}}"
                                            values={{ totalReviews }}
                                        />,
                                    ]}
                                    {!!reviewsWithPhotosCount && [
                                        PHOTOS_TAB,
                                        <FormattedMessage
                                            key={PHOTOS_TAB}
                                            id="SellerReviewsModalTabs.photosTab"
                                            defaultMessage="Photos"
                                        />,
                                    ]}
                                </TabList>
                            </div>
                        )}

                        {/* Show only when data is loaded */}
                        {typeof totalReviews === 'number' && !noTabs && (
                            <div
                                className={classnames(styles.hr, {
                                    [styles.legacyStyle]: !feedbackFormPhotoUpload,
                                })}
                            />
                        )}
                    </div>
                </>
            )}
        </HeadingLevel>
    );
};
